import flatten from 'lodash/flatten';
import isNull from 'lodash/isNull';

import {
    EhrData,
    EhrResponse,
    EhrUpdateForm,
    EhrCreateForm,
    EhrDataListItem,
    EhrFieldsData,
    EhrFieldsResponse,
    EhrMobileAppStatusData,
    EhrFieldsVersionParams,
    ServicePlanListItemData,
    ServicePlanListItemResponse,
    EhrListResponse,
    EhrReferralPatientListResponse,
    EhrReferralPatientSearchQuery,
    CardData,
    EhrCardsDataResponse,
    EhrSearchQuery,
    EhrFieldsVersionResponse,
    EhrEntryCount,
    EhrsEntryCountResponse,
    EhrMergeData,
    EhrsRenderedServiceCountResponse,
    FileViewModel,
    EntityFileData,
    EhrBlockParams,
    EhrPlanListQuery,
    CalculateCostParams,
    CalculateCostResponse,
    EhrChatQuery,
} from 'chaika-frontend-types/frontend-cms';
import { EhrViewContext, EHR_VIEW_CONTEXT_HEADER, Locale, RenderedServiceStatus } from 'chaika-enums';

import { axios } from '@lib/axios';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { EhrChatResponse, CrossCountrySummaryViewModel } from 'chaika-types';

const BASE_URL = '/api/ehr';

interface GetServicePlansParams extends Omit<EhrPlanListQuery, 'actualOn' | 'serviceIds'> {
    actualOn: string;
    serviceIds: string[];
}

export class EhrApi {
    public static async get(id: number | string, viewContext?: EhrViewContext): Promise<EhrData> {
        const headers = viewContext ? { [EHR_VIEW_CONTEXT_HEADER]: viewContext } : undefined;
        const res = await axios.get<EhrResponse>(`${BASE_URL}/${id}`, { headers, skipForbiddenPopup: true });

        return res.data.ehr;
    }

    public static async findSimilarForEhrs(ids: string[]): Promise<{ [id: string]: EhrListResponse }> {
        const res = await axios.get(`${BASE_URL}/similar`, { params: { ids: ids.join(',') } });

        return res.data;
    }

    public static async findSimilar(id: number | string): Promise<EhrDataListItem[]> {
        const res = await axios.get<EhrListResponse>(`${BASE_URL}/${id}/similar`);

        return res.data.ehrs;
    }

    public static async getEhrFieldDate(id: string): Promise<EhrFieldsData> {
        const res = await axios.get<EhrFieldsResponse>(`${BASE_URL}/${id}/field`, { skipForbiddenPopup: true });

        return res.data.ehrFields;
    }

    public static async sendRegistrationUrl(id: string, locale?: Locale): Promise<void> {
        await axios.post(`${BASE_URL}/${id}/registration-url/`, {
            locale,
        });
    }

    public static async sendConfirmationCode(id: string, locale?: Locale): Promise<void> {
        await axios.post(`${BASE_URL}/${id}/registration-url/sms-code`, {
            locale,
        });
    }

    public static async getEhrsEntryCount(ehrIds: string[]): Promise<EhrEntryCount[]> {
        const res = await axios.get<EhrsEntryCountResponse>(`${BASE_URL}/entry/count`, {
            params: { ehrIds: ehrIds.join(',') },
        });

        return res.data.ehrsEntryCount;
    }

    public static async getEhrCards(id: string): Promise<CardData[]> {
        const res = await axios.get<EhrCardsDataResponse>(`${BASE_URL}/${id}/bank-card`);

        return res.data.cards;
    }

    public static async getEhrFieldHistory(id: string, fieldNames?: string[]): Promise<EhrFieldsVersionParams[]> {
        const names = fieldNames ? fieldNames.join(',') : undefined;

        const res = await axios.get<EhrFieldsVersionResponse>(`${BASE_URL}/${id}/field/history`, {
            params: { names },
            skipForbiddenPopup: true,
        });

        return res.data.fieldVersions;
    }

    public static async update(id: number | string, ehr: EhrUpdateForm): Promise<void> {
        await axios.put(`${BASE_URL}/${id}`, { ehr });
    }

    public static async create(ehr: EhrCreateForm): Promise<void> {
        await axios.post(BASE_URL, { ehr });
    }

    public static async merge(ehr: EhrMergeData): Promise<void> {
        await axios.post(`${BASE_URL}/merge`, { ehr });
    }

    public static async find({ limit = 200, ...params }: EhrSearchQuery): Promise<EhrDataListItem[]> {
        const res = await axios.get<EhrListResponse>(BASE_URL, { params: { ...params, limit } });

        return res.data.ehrs;
    }

    public static async findWithCount({ limit = 200, ...params }: EhrSearchQuery): Promise<EhrListResponse> {
        const res = await axios.get<EhrListResponse>(BASE_URL, { params: { ...params, limit } });

        return res.data;
    }

    public static async searchReferralPatients(
        params: EhrReferralPatientSearchQuery,
    ): Promise<EhrReferralPatientListResponse> {
        const res = await axios.get<EhrReferralPatientListResponse>(`${BASE_URL}/referral-patients`, { params });

        return res.data;
    }

    public static async getServicePlans(
        ehrId: string,
        { serviceIds, actualOn, onlyAvailable }: GetServicePlansParams,
    ): Promise<ServicePlanListItemData[]> {
        const chunkedIds = getUniqChunkedIds(serviceIds);
        const responses = await Promise.all(
            chunkedIds.map(ids =>
                axios.get<ServicePlanListItemResponse>(`${BASE_URL}/${ehrId}/plan`, {
                    params: { actualOn, onlyAvailable, serviceIds: ids.join() },
                }),
            ),
        );

        return flatten(responses.map(res => res.data.servicesPlan));
    }

    public static async getEhrsRenderedServiceCount(
        ehrIds: string[],
        status: RenderedServiceStatus[],
        withoutFullInsurance: boolean,
    ): Promise<EhrsRenderedServiceCountResponse> {
        const res = await axios.get<EhrsRenderedServiceCountResponse>(`${BASE_URL}/rendered-service/count`, {
            params: { status, withoutFullInsurance, ehrIds: ehrIds.join(',') },
        });

        return res.data;
    }

    public static async getFiles(ehrId: string): Promise<EntityFileData[]> {
        const { data } = await axios.get(`${BASE_URL}/${ehrId}/file`);

        return data.files;
    }

    public static async addFile(ehrId: string, fileName: string): Promise<FileViewModel> {
        const url = `${BASE_URL}/${ehrId}/file`;
        const { data } = await axios.post(url, { fileName });

        return data;
    }

    public static async setFileUploadedStatus(ehrId: string, fileName: string): Promise<void> {
        const url = `${BASE_URL}/${ehrId}/file/${fileName}/status/uploaded`;
        await axios.put(url);
    }

    public static async deleteFile(ehrId: string, fileName: string): Promise<void> {
        const url = `${BASE_URL}/${ehrId}/file/${fileName}`;
        await axios.delete(url);
    }

    public static async block(ehrId: string, params: EhrBlockParams): Promise<void> {
        const url = `${BASE_URL}/${ehrId}/block`;
        await axios.post(url, {
            ehr: params,
        });
    }

    public static async unblock(ehrId: string): Promise<void> {
        const url = `${BASE_URL}/${ehrId}/block`;
        await axios.delete(url);
    }

    public static async deleteNotExistsEntryFromElasticCommand(id: string): Promise<void> {
        await axios.delete(`${BASE_URL}/${id}/not-exist-entries`);
    }

    public static async calculateCostForEhr(
        ehrId: string,
        params: CalculateCostParams,
    ): Promise<CalculateCostResponse[]> {
        try {
            const organizationId = isNull(params.organizationId) ? undefined : params.organizationId;
            const userId = isNull(params.userId) ? undefined : params.userId;
            const { data } = await axios.post(`/api/ehr/${ehrId}/cost`, {
                ignoreOutDateSubscriptions: true,
                ...params,
                organizationId,
                userId,
            });

            return data;
        } catch (e: any) {
            console.error('Error on calculateCostForEhr:', e.message);
            return [];
        }
    }

    public static async findChat(ehrId: string, params: EhrChatQuery = {}): Promise<EhrChatResponse> {
        const res = await axios.get(`${BASE_URL}/${ehrId}/chat`, { params });

        return res.data;
    }

    public static async createChatWithDoctor(ehrId: string, doctorId: string): Promise<void> {
        await axios.post(`${BASE_URL}/${ehrId}/chat/doctor/${doctorId}`);
    }

    public static async createChatWithPractice(ehrId: string, practiceId: string): Promise<void> {
        await axios.post(`${BASE_URL}/${ehrId}/chat/practice/${practiceId}`);
    }

    public static async getMobileAppStatus(ehrId: string): Promise<EhrMobileAppStatusData> {
        const res = await axios.get<EhrMobileAppStatusData>(`${BASE_URL}/${ehrId}/mobile-app`);

        return res.data;
    }

    public static async getMedicalCaseCrossCountrySummary(id: string): Promise<CrossCountrySummaryViewModel[]> {
        const res = await axios.get<CrossCountrySummaryViewModel[]>(
            `${BASE_URL}/${id}/medical-case/cross-country-summary`,
            { skipForbiddenPopup: true },
        );

        return res.data;
    }

    public static async getEntryCrossCountrySummary(id: string): Promise<CrossCountrySummaryViewModel[]> {
        const res = await axios.get<CrossCountrySummaryViewModel[]>(`${BASE_URL}/${id}/entry/cross-country-summary`, {
            skipForbiddenPopup: true,
        });

        return res.data;
    }
}

export { BASE_URL as EHR_BASE_URL };
